import { Target, TrendingUp, TrendingDown, Clock, Activity, Gauge, RotateCw, Zap } from "lucide-react";
import { DrillBitIcon } from "@/assets/DrillBitIcon";
import { MetresPerShiftIcon } from "@/assets/MetresPerShiftIcon";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export interface DrillingMetrics {
  totalHoles: number;
  totalMetres: number;
  d65Metres: number;
  md6250Metres: number;
  totalDrillingSeconds: number;
  avgDepth: number;
  avgTimePerHole: number;
  avgMetresPerBit: number;
  avgMetresPerBit165: number;
  avgMetresPerBit203: number;
  penetrationRate: number;
  redrillHoles: number;
  redrillPct: number;
  drillAvailability: number;
}

export interface TrendMetrics {
  totalMetres?: number;
  avgMetresPerShift?: number;
  penetrationRate?: number;
  drillAvailability?: number;
  totalDelaySeconds?: number;
  redrillPct?: number;
}

interface DelayMetrics {
  totalDelaySeconds: number;
  totalDelayCount: number;
}

interface DrillingMetricsCardsProps {
  drillingMetrics: DrillingMetrics;
  delayMetrics: DelayMetrics;
  shiftCount: number;
  appName?: string;
  prevMetrics?: TrendMetrics;
}

function formatTime(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function Trend({
  current,
  prev,
  invertColor = false,
}: {
  current: number;
  prev?: number;
  invertColor?: boolean;
}) {
  if (!prev || prev === 0 || current === 0) return null;
  const pct = ((current - prev) / prev) * 100;
  if (Math.abs(pct) < 0.5) return null;
  const isUp = pct > 0;
  const isGood = invertColor ? !isUp : isUp;
  return (
    <span className={`text-xs font-medium flex items-center gap-0.5 ${isGood ? "text-green-500" : "text-red-500"}`}>
      {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

export default function DrillingMetricsCards({
  drillingMetrics,
  delayMetrics,
  shiftCount,
  appName,
  prevMetrics,
}: DrillingMetricsCardsProps) {
  const avgMetresPerShift = shiftCount > 0
    ? drillingMetrics.totalMetres / shiftCount
    : 0;

  const prevAvgMetresPerShift =
    prevMetrics?.avgMetresPerShift !== undefined ? prevMetrics.avgMetresPerShift : undefined;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {/* Total Holes */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Target className="h-4 w-4" />
            Total Holes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold text-foreground">{drillingMetrics.totalHoles.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Avg depth: {drillingMetrics.avgDepth.toFixed(1)}m</p>
        </CardContent>
      </Card>

      {/* Total Metres */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Total Metres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2">
            <p className="text-2xl font-bold text-foreground">{Math.round(drillingMetrics.totalMetres).toLocaleString()}m</p>
            <Trend current={drillingMetrics.totalMetres} prev={prevMetrics?.totalMetres} />
          </div>
          <p className="text-xs text-muted-foreground">Drilling time: {formatTime(drillingMetrics.totalDrillingSeconds)}</p>
        </CardContent>
      </Card>

      {/* Penetration Rate */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Gauge className="h-4 w-4" />
            Penetration Rate
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2">
            <p className="text-2xl font-bold text-foreground">
              {drillingMetrics.penetrationRate > 0 ? `${drillingMetrics.penetrationRate.toFixed(1)}` : "—"}
            </p>
            {drillingMetrics.penetrationRate > 0 && (
              <span className="text-sm text-muted-foreground mb-0.5">m/hr</span>
            )}
            <Trend current={drillingMetrics.penetrationRate} prev={prevMetrics?.penetrationRate} />
          </div>
          <p className="text-xs text-muted-foreground">avg across all holes</p>
        </CardContent>
      </Card>

      {/* Drill Availability */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Drill Availability
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2">
            <p className="text-2xl font-bold text-foreground">
              {drillingMetrics.drillAvailability > 0 ? `${drillingMetrics.drillAvailability.toFixed(1)}%` : "—"}
            </p>
            <Trend current={drillingMetrics.drillAvailability} prev={prevMetrics?.drillAvailability} />
          </div>
          <p className="text-xs text-muted-foreground">shift hrs minus delays</p>
        </CardContent>
      </Card>

      {/* D65 Metres */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            D65 Metres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold text-foreground">{Math.round(drillingMetrics.d65Metres).toLocaleString()}m</p>
          <p className="text-xs text-muted-foreground">165mm holes</p>
        </CardContent>
      </Card>

      {/* MD6250 Metres */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            MD6250 Metres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold text-foreground">{Math.round(drillingMetrics.md6250Metres).toLocaleString()}m</p>
          <p className="text-xs text-muted-foreground">203mm holes</p>
        </CardContent>
      </Card>

      {/* Total Downtime */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Total Downtime
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2">
            <p className="text-2xl font-bold text-foreground">{formatTime(delayMetrics.totalDelaySeconds)}</p>
            <Trend current={delayMetrics.totalDelaySeconds} prev={prevMetrics?.totalDelaySeconds} invertColor />
          </div>
          <p className="text-xs text-muted-foreground">{delayMetrics.totalDelayCount} delay events</p>
        </CardContent>
      </Card>

      {/* Shift Logs */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Shift Logs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold text-foreground">{shiftCount}</p>
          <p className="text-xs text-muted-foreground">{appName ?? "Operator app"}</p>
        </CardContent>
      </Card>

      {/* Avg m/Shift */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <MetresPerShiftIcon className="h-4 w-4" />
            Avg m / Shift
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2">
            <p className="text-2xl font-bold text-foreground">{avgMetresPerShift.toLocaleString(undefined, { maximumFractionDigits: 1 })}m</p>
            <Trend current={avgMetresPerShift} prev={prevAvgMetresPerShift} />
          </div>
          <p className="text-xs text-muted-foreground">all rigs combined</p>
        </CardContent>
      </Card>

      {/* Avg m/Bit 165mm */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <DrillBitIcon className="h-4 w-4" />
            Avg m / Bit (165mm)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold text-foreground">
            {drillingMetrics.avgMetresPerBit165 > 0 ? `${Math.round(drillingMetrics.avgMetresPerBit165).toLocaleString()}m` : "—"}
          </p>
          <p className="text-xs text-muted-foreground">165mm drill bits</p>
        </CardContent>
      </Card>

      {/* Avg m/Bit 203mm */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <DrillBitIcon className="h-4 w-4" />
            Avg m / Bit (203mm)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold text-foreground">
            {drillingMetrics.avgMetresPerBit203 > 0 ? `${Math.round(drillingMetrics.avgMetresPerBit203).toLocaleString()}m` : "—"}
          </p>
          <p className="text-xs text-muted-foreground">203mm drill bits</p>
        </CardContent>
      </Card>

      {/* Redrills */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <RotateCw className="h-4 w-4" />
            Redrills
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2">
            <p className={`text-2xl font-bold ${drillingMetrics.redrillPct > 5 ? "text-red-500" : "text-foreground"}`}>
              {drillingMetrics.redrillHoles > 0 ? `${drillingMetrics.redrillPct.toFixed(1)}%` : "0%"}
            </p>
            <Trend current={drillingMetrics.redrillPct} prev={prevMetrics?.redrillPct} invertColor />
          </div>
          <p className="text-xs text-muted-foreground">{drillingMetrics.redrillHoles} of {drillingMetrics.totalHoles} holes redrilled</p>
        </CardContent>
      </Card>
    </div>
  );
}
